import { defineStore } from "pinia";
import { ref } from "vue";
import { type IconReactionType, ReactionTargetType } from "@/API";
import {
  type Comment,
  type DbReaction,
  type IconReaction,
  type Message,
  normalizeIconReaction,
  type Topic,
} from "@/model";
import ReactionDao from "./reaction-dao";

type ReactionsState = Record<string, IconReaction | undefined>;

export type PayloadFetchTopicList = {
  domainId: string;
  type: IconReactionType;
};
export type PayloadFetchMessageList = {
  domainId: string;
  topicId: string;
  type: IconReactionType;
};
type PayloadSet = { data: { type: IconReactionType; reaction: DbReaction } };
type PayloadSetAll = { data: { reactions: IconReaction[] } };
export type PayloadUpdateReaction = {
  type: IconReactionType;
  target: Topic | Message | Comment;
  userDirectId: string;
  value: boolean;
};

export type ListTopics = {
  domainId: string;
  topicId?: undefined;
  messageId?: undefined;
  commentId?: undefined;
};
export type ListMessages = {
  domainId: string;
  topicId: string;
  messageId?: undefined;
  commentId?: undefined;
};
export type ListComments = {
  domainId: string;
  topicId: string;
  messageId: string;
  commentId?: undefined;
};
export type ListTarget = ListTopics | ListMessages | ListComments;

export type FindByTopic = {
  domainId: string;
  topicId: string;
  messageId?: never;
  commentId?: never;
};
export type FindByMessage = {
  domainId: string;
  topicId: string;
  messageId: string;
  commentId?: never;
};
export type FindByComment = {
  domainId: string;
  topicId: string;
  messageId: string;
  commentId: string;
};
export type FindReaction = FindByTopic | FindByMessage | FindByComment;

export type ReactionGetter = (
  type: IconReactionType,
  target: FindReaction,
) => IconReaction | undefined;
export type ReactionHasUser = (
  type: IconReactionType,
  target: FindReaction,
  userId: string,
) => boolean;

// storeに保存するときのキー形式
function createKeyForSet(
  type: IconReactionType,
  target: IconReaction | DbReaction,
): string {
  switch (target.targetType) {
    case ReactionTargetType.TOPIC:
      return `${type}#${target.targetType}#${target.domainId}#${target.topicId}`;
    case ReactionTargetType.MESSAGE:
      return `${type}#${target.targetType}#${target.domainId}#${target.topicId}#${target.messageId}`;
    default:
      return `${type}#${target.targetType}#${target.domainId}#${target.topicId}#${target.messageId}#${target.commentId}`;
  }
}

// TargetTypeを取得
function getListTargetType(target: ListTarget): ReactionTargetType {
  if (target.topicId === undefined) return ReactionTargetType.TOPIC;
  if (target.messageId === undefined) return ReactionTargetType.MESSAGE;
  return ReactionTargetType.COMMENT;
}

// TargetTypeを取得
function getFindTargetType(target: FindReaction): ReactionTargetType {
  if (target.messageId === undefined) return ReactionTargetType.TOPIC;
  if (target.commentId === undefined) return ReactionTargetType.MESSAGE;
  return ReactionTargetType.COMMENT;
}

// store から読み出す時のキーを作成
function createListKey(
  type: IconReactionType,
  target: ListTarget,
  targetType?: ReactionTargetType,
): string {
  targetType = targetType ? targetType : getListTargetType(target);

  switch (targetType) {
    case ReactionTargetType.TOPIC:
      return `${type}#${targetType}#${target.domainId}`;
    case ReactionTargetType.MESSAGE:
      return `${type}#${targetType}#${target.domainId}#${target.topicId}`;
    case ReactionTargetType.COMMENT:
      return `${type}#${targetType}#${target.domainId}#${target.topicId}#${target.messageId}`;
    default:
      return `${type}#${targetType}#${target.domainId}#${target.topicId}#${target.messageId}#${target.commentId}`;
  }
}

function createFindKey(type: IconReactionType, target: FindReaction): string {
  const targetType = getFindTargetType(target);
  switch (targetType) {
    case ReactionTargetType.TOPIC:
      return `${type}#${targetType}#${target.domainId}#${target.topicId}`;
    case ReactionTargetType.MESSAGE:
      return `${type}#${targetType}#${target.domainId}#${target.topicId}#${target.messageId}`;
    case ReactionTargetType.COMMENT:
      return `${type}#${targetType}#${target.domainId}#${target.topicId}#${target.messageId}#${target.commentId}`;
    default:
      return ``;
  }
}

export const useReactionStore = defineStore("reactions", () => {
  const reactions = ref<ReactionsState>({});

  // 条件に合うリアクション一覧を取得
  const listReactions = (
    type: IconReactionType,
    target: ListTarget,
    targetType?: ReactionTargetType,
  ): IconReaction[] => {
    const key = createListKey(type, target, targetType);
    const keys = Object.keys(reactions.value).filter((id) =>
      id.startsWith(key),
    );
    return keys
      .map((k) => reactions.value[k])
      .filter((reaction) => !!reaction) as IconReaction[];
  };

  // --- getters ---
  const get = (
    type: IconReactionType,
    target: FindReaction,
  ): IconReaction | undefined => {
    const key = createFindKey(type, target);
    return reactions.value[key];
  };

  const listBy = (
    type: IconReactionType,
    target: ListTarget,
    targetType: ReactionTargetType,
  ): Record<string, IconReaction> => {
    if (!target) return {};
    const list = listReactions(type, target, targetType);
    const obj: Record<string, IconReaction> = {};
    return list.reduce((prev, reaction) => {
      switch (reaction.targetType) {
        case ReactionTargetType.TOPIC:
          prev[reaction.topicId] = reaction;
          break;
        case ReactionTargetType.MESSAGE:
          prev[reaction.messageId] = reaction;
          break;
        case ReactionTargetType.COMMENT:
          prev[reaction.commentId] = reaction;
          break;
      }
      return prev;
    }, obj);
  };

  const hasReaction = (
    type: IconReactionType,
    target: FindReaction,
    userId: string,
  ): boolean => {
    const key = createFindKey(type, target);
    const reaction = reactions.value[key];
    return reaction
      ? reaction.userList.some((u) => u.userId === userId)
      : false;
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const setMutation = (payload: PayloadSet): void => {
    const key = createKeyForSet(payload.data.type, payload.data.reaction);
    const dbReaction = payload.data.reaction;
    const reaction = reactions.value[key];
    if (reaction) {
      const index = reaction.userList.findIndex(
        (u) => u.userId === dbReaction.userId,
      );
      if (index < 0) {
        if (dbReaction.deletion === 0) {
          // ユーザーを追加
          reaction.userList.push({
            userId: dbReaction.userId,
            updatedAt: dbReaction.updatedAt,
          });
        }
      } else {
        if (dbReaction.deletion === 0) {
          reaction.userList[index].updatedAt = dbReaction.updatedAt;
        } else {
          // ユーザーを削除
          reaction.userList.splice(index, 1);
        }
      }
    } else if (dbReaction.deletion === 0) {
      // リアクションを追加
      const newReaction = normalizeIconReaction(dbReaction);
      reactions.value = { ...reactions.value, [key]: newReaction };
    }
  };

  const setAllMutation = (payload: PayloadSetAll): void => {
    const list = payload.data.reactions;
    const updates: Record<string, IconReaction> = {};
    for (const reaction of list) {
      const key = createKeyForSet(reaction.type, reaction);
      updates[key] = reaction;
    }
    reactions.value = { ...reactions.value, ...updates };
  };

  // --- actions ---
  const fetchDomainTopics = async ({
    domainId,
  }: PayloadFetchTopicList): Promise<void> => {
    const results = await ReactionDao.readTopicReactions(domainId);
    if (results) {
      setAllMutation({ data: { reactions: results } });
    }
  };

  const fetchMessageAndCommentReactions = async ({
    domainId,
    topicId,
  }: {
    domainId: string;
    topicId: string;
  }): Promise<void> => {
    const results = await ReactionDao.readMessageAndCommentReactions(
      domainId,
      topicId,
    );
    if (results) {
      const list = [...results.messageReactions, ...results.commentReactions];
      setAllMutation({ data: { reactions: list } });
    }
  };

  const update = async ({
    type,
    target,
    value,
  }: PayloadUpdateReaction): Promise<void> => {
    const result = await ReactionDao.upsert(target, type, value);
    if (result) {
      setMutation({ data: { type: result.type, reaction: result } });
    }
  };

  const registerSubscription = async (payload: {
    domainId: string;
  }): Promise<void> => {
    const domainId = payload.domainId;
    try {
      await ReactionDao.registerSubscriptions(domainId, {
        onCreate: (reaction: DbReaction) => {
          setMutation({ data: { type: reaction.type, reaction: reaction } });
        },
        onUpdate: (reaction: DbReaction) => {
          setMutation({ data: { type: reaction.type, reaction: reaction } });
        },
        onDelete: () => {
          // 何もしない
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const removeSubscription = async (payload: {
    domainId: string;
  }): Promise<void> => {
    await ReactionDao.closeSubscription(payload.domainId);
  };

  return {
    reactions,
    get,
    listBy,
    hasReaction,
    fetchDomainTopics,
    fetchMessageAndCommentReactions,
    update,
    registerSubscription,
    removeSubscription,
  };
});

export default useReactionStore;
